/**
 * MarketplaceBreakdown Component - Enhanced marketplace-brand performance analysis
 * Fixed table header disappearing, column alignment, and commission rate accuracy
 * Now supports brand-marketplace pairs with proper commission overrides
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Box,
  Text,
  BlockStack,
  InlineStack,
  Divider,
  IndexTable,
  Spinner,
  EmptyState
} from '@shopify/polaris';
import { useMarketplaceAnalytics, useBrands } from '../../utils/queries';
import apiClient from '../../utils/api';

export default function MarketplaceBreakdown({
  brandId = 'all',
  period = 30,
  dateRange = null,
  title = 'Marketplace Performance',
  showFilters = true,
  compact = false,
  onMarketplaceSelect,
  ...props
}) {
  const [sortBy, setSortBy] = useState('netSales');
  const [sortDirection, setSortDirection] = useState('descending');

  // Build query params based on whether we have a date range or period
  const queryParams = useMemo(() => {
    const params = { brand: brandId };

    if (dateRange && dateRange.startDate && dateRange.endDate) {
      // Use custom date range
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    } else {
      // Use period (days)
      params.days = period;
    }

    // Add sorting parameters for backend
    params.sortBy = sortBy;
    params.sortOrder = sortDirection === 'ascending' ? 'asc' : 'desc';

    return params;
  }, [brandId, period, dateRange, sortBy, sortDirection]);

  // Fetch both marketplace analytics data and brands data
  const marketplaceQuery = useMarketplaceAnalytics(queryParams);

  const brandsQuery = useBrands();

  const {
    data: marketplaceData = [],
    isLoading: marketplaceLoading = false,
    error: marketplaceError = null,
    refetch: refetchMarketplace = () => { },
  } = marketplaceQuery || {};

  const {
    data: brandsResponse = [],
    isLoading: brandsLoading = false,
  } = brandsQuery || {};

  // Robust brands data parsing
  const parseBrandsResponse = (response) => {
    try {
      if (!response) return [];

      // Handle direct array
      if (Array.isArray(response)) {
        return response.filter(brand => brand && typeof brand === 'object' && brand.id);
      }

      // Handle object with brands array
      if (typeof response === 'object') {
        const brands = response.brands || response.data || response.items || [];
        return Array.isArray(brands) ? brands.filter(brand => brand && typeof brand === 'object' && brand.id) : [];
      }

      return [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplaceBreakdown: Error parsing brands response:', error);
      }
      return [];
    }
  };

  const brands = parseBrandsResponse(brandsResponse);

  // Create brand lookup for quick access with error handling
  const brandLookup = useMemo(() => {
    try {
      const lookup = new Map();
      if (Array.isArray(brands)) {
        brands.forEach(brand => {
          if (brand && brand.id) {
            lookup.set(brand.id, brand);
          }
        });
      }
      return lookup;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplaceBreakdown: Error creating brand lookup:', error);
      }
      return new Map();
    }
  }, [brands]);

  // Process ONLY real API data - no demo fallback
  const processedData = useMemo(() => {
    return marketplaceData;
  }, [marketplaceData]);

  // Index table resource state
  const resourceName = {
    singular: 'marketplace',
    plural: 'marketplaces',
  };

  // Handle sort changes - Updated to match standardized column order
  // Removed: Weight, Discounts, Commission Rate. Added: Items, AOV. Renamed: Gross Revenue → Sales
  const handleSort = useCallback((headingIndex, direction) => {
    console.log('direction: ', direction);
    // Map heading index to backend sort field - standardized columns
    const sortOptions = [
      'brandName',      // 0: Brand
      'marketplace',    // 1: Marketplace
      'orderCount',     // 2: Orders
      'totalItems',     // 3: Items (NEW)
      'aov',            // 4: AOV (NEW)
      'sales',          // 5: Sales (was Gross Revenue)
      'refunds',        // 6: Refunds
      'netSales',       // 7: Net Sales
      'commission',     // 8: Commission
      'shippingFees',   // 9: Shipping Fees
      'returnFees',     // 10: Return Fees
      'netPayout'       // 11: Net Payout
    ];

    setSortBy(sortOptions[headingIndex]);
    setSortDirection(direction);
  }, []);


  // Combined loading state
  const isLoading = marketplaceLoading || brandsLoading;

  // Table headings - Standardized column order
  // Removed: Weight, Discounts, Commission Rate. Added: Items, AOV. Renamed: Gross Revenue → Sales
  const tableHeadings = [
    { title: 'Brand', alignment: 'start' },
    { title: 'Marketplace', alignment: 'start' },
    { title: 'Orders', alignment: 'end' },
    { title: 'Items', alignment: 'end' },
    { title: 'AOV', alignment: 'end' },
    { title: 'Sales', alignment: 'end' },
    { title: 'Refunds', alignment: 'end' },
    { title: 'Net Sales', alignment: 'end' },
    { title: 'Commission', alignment: 'end' },
    { title: 'Shipping Fees', alignment: 'end' },
    { title: 'Return Fees', alignment: 'end' },
    { title: 'Net Payout', alignment: 'end' },
  ];

  // Simple row rendering - Standardized columns
  // Removed: Weight, Discounts, Commission Rate. Added: Items, AOV. Renamed: Gross Revenue → Sales
  const rowMarkup = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return null;
    }
    return processedData.map((item, index) => (
      <IndexTable.Row
        id={item.id}
        key={item.id}
        position={index}
        onClick={() => onMarketplaceSelect?.(item)}
      >
        {/* Brand column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {item.brandName || ''}
          </Text>
        </IndexTable.Cell>

        {/* Marketplace column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="semibold">
            {item.marketplace}
          </Text>
        </IndexTable.Cell>

        {/* Orders column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(item.orderCount || 0).toLocaleString()}
          </Text>
        </IndexTable.Cell>

        {/* Items column (NEW) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(item.totalItems || 0).toLocaleString()}
          </Text>
        </IndexTable.Cell>

        {/* AOV column (NEW) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(() => {
              try {
                return apiClient.formatCurrency(item.aov || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Sales column (was Gross Revenue) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(() => {
              try {
                return apiClient.formatCurrency(item.sales || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Refunds column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(() => {
              try {
                return apiClient.formatCurrency(item.refunds || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Net Sales column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end" fontWeight="medium">
            {(() => {
              try {
                return apiClient.formatCurrency(item.netSales || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Commission column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end" fontWeight="medium">
            {(() => {
              try {
                return apiClient.formatCurrency(item.commission || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Shipping Fees column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(() => {
              try {
                return apiClient.formatCurrency(item.shippingFees || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Return Fees column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end">
            {(() => {
              try {
                return apiClient.formatCurrency(item.returnFees || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Net Payout column - final amount merchant gets */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="end" fontWeight="semibold">
            {(() => {
              try {
                return apiClient.formatCurrency(item.netPayout || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>
      </IndexTable.Row>
    ));
  }, [processedData, onMarketplaceSelect]);

  // CONDITIONAL RETURNS - AFTER ALL HOOKS
  if (isLoading) {
    return (
      <Card {...props}>
        <Box padding="400">
          <BlockStack gap="400" align="center">
            <Spinner size="large" />
            <BlockStack gap="200" align="center">
              <Text variant="bodyLg">Loading marketplace data</Text>
              <Text variant="bodySm" tone="subdued">
                Analyzing performance across sales channels and brands
              </Text>
            </BlockStack>
          </BlockStack>
        </Box>
      </Card>
    );
  }

  // Error state
  if (marketplaceError) {
    return (
      <Card {...props}>
        <Box padding="400">
          <EmptyState
            heading="Unable to load marketplace data"
            content={marketplaceError.message || 'There was an error loading the marketplace breakdown. Please try refreshing the page.'}
            action={{
              content: 'Retry',
              onAction: refetchMarketplace,
            }}
            image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
          />
        </Box>
      </Card>
    );
  }

  // Empty state - improved messaging based on data availability
  if (!processedData || processedData.length === 0) {
    const emptyMessage = marketplaceError
      ? "Failed to load marketplace data. Please check your connection and try again."
      : marketplaceData === null || marketplaceData === undefined
        ? "Loading marketplace data..."
        : Array.isArray(marketplaceData) && marketplaceData.length === 0
          ? "No marketplace data found. This could mean there are no processed orders yet, or the marketplace detection system needs configuration."
          : "Marketplace performance data will appear here once orders are processed and tagged with marketplace information.";

    return (
      <Card {...props}>
        <Box padding="400">
          <EmptyState
            heading="No marketplace data available"
            content={emptyMessage}
            image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
            action={marketplaceError ? {
              content: 'Retry',
              onAction: refetchMarketplace,
            } : undefined}
          />
        </Box>
      </Card>
    );
  }

  return (
    <Card {...props}>
      <Box padding="0">
        {/* Enhanced Header with proper alignment */}
        <Box padding="600" paddingBlockEnd="400">
          <BlockStack gap="100">
            <Text variant="headingMd">
              Cross-Brand Marketplace Analytics
            </Text>
            <Text variant="bodySm" tone="subdued">
              Performance breakdown across {processedData.length} sales channels • Click column headers to sort
            </Text>
          </BlockStack>
        </Box>

        <Divider />

        {/* Enhanced Table with proper configuration */}
        <Box padding="0">
          <IndexTable
            resourceName={resourceName}
            itemCount={processedData.length}
            selectable={false}
            headings={tableHeadings}
            onSort={handleSort}
            sortable={tableHeadings.map(() => true)}
            sortDirection={sortDirection}
            sortColumnIndex={(() => {
              // Map backend sort field to column index - standardized columns
              const mapping = {
                'brandName': 0,
                'marketplace': 1,
                'orderCount': 2,
                'totalItems': 3,
                'aov': 4,
                'sales': 5,
                'refunds': 6,
                'netSales': 7,
                'commission': 8,
                'shippingFees': 9,
                'returnFees': 10,
                'netPayout': 11
              };
              return mapping[sortBy] !== undefined ? mapping[sortBy] : 7; // Default to Net Sales
            })()}
            hasMoreItems={false}
            condensed={compact}
          >
            {rowMarkup}
          </IndexTable>
        </Box>
      </Box>
    </Card>
  );
}